import React from 'react';
import { navigate } from '@gatsbyjs/reach-router';
import { TableHeaderDefinitionCellType } from '~/components/organism/Table/types';
import TEST_ID from './index.testid';
import TableHeaderCell from '~/components/organism/Table/components/Header/utils';
import {
  SortDirection,
  type FlowV2_ConditionListFragment,
  type FlowV2_Statistics,
} from '~/graphql/types';
import { formattedDateWithMonth } from '~/util/date';
import StatisticsProgressBar from '~/components/molecule/StatisticsProgressBar';
import EnabledStatusIndicator from '~/components/atom/EnabledStatusIndicator';
import ShareIndicator from '~/components/organism/ShareIndicator';
import DateCell from '~/components/organism/Table/components/DateCell';
import memoize from 'lodash/memoize';
import TriggerColumn from '../TriggerColumn';
import ErrorsColumn from '../ErrorsColumn';
import type { IBuilderContext } from '../../../Builder/context';

export type SortToggleFunction = (
  sortField: Array<string>,
  sortDirection: SortDirection | null,
) => void;

const text = {
  notModifiedMessage: 'Nooit',
};

const flowListColumns = memoize(
  (
    sortToggle: SortToggleFunction,
    version: string,
    hasErrors: boolean,
  ): Array<TableHeaderDefinitionCellType<any>> => {
    const columns = [
      TableHeaderCell.checkbox({
        dataTestId: TEST_ID.CHECKBOX,
      }),
      TableHeaderCell.string({
        label: 'Naam',
        dataProperty: 'name',
        width: 230,
        dataTestId: TEST_ID.NAME,
        sorting: {
          onSort: sortDirection => sortToggle(['name'], sortDirection),
        },
      }),
      TableHeaderCell.custom({
        label: 'Trigger',
        dataProperty: 'trigger',
        width: 200,
        dataTestId: TEST_ID.TRIGGER,
        sorting: {
          onSort: sortDirection => sortToggle(['trigger'], sortDirection),
        },
        version,
        renderFunction: (value: {
          iBuilderContext: IBuilderContext;
          actionId: string;
          conditionList?: FlowV2_ConditionListFragment;
        }) => <TriggerColumn {...value} />,
      }),
      /** This is only added to access flow description when copying flow-v2 */
      TableHeaderCell.string({
        label: 'Description',
        dataProperty: 'description',
        sorting: {
          onSort: sortDirection => sortToggle(['description'], sortDirection),
        },
        isResizable: false,
        width: 0,
        dataTestId: TEST_ID.DESCRIPTION,
      }),
      TableHeaderCell.custom<FlowV2_Statistics>({
        label: 'Resultaten',
        dataProperty: 'Statistics',
        dataTestId: TEST_ID.STATISTICS,
        width: 160,
        renderFunction: value => (
          <StatisticsProgressBar
            completedAmount={value.completedFlowInstancesTotal}
            nonCompletedAmount={value.nonCompletedFlowInstancesTotal}
          />
        ),
        sorting: {
          onSort: sortDirection => sortToggle(['Statistics'], sortDirection),
        },
        version,
      }),
      TableHeaderCell.date({
        label: 'Aangemaakt',
        dataProperty: 'createdDate',
        dataTestId: TEST_ID.CREATED,
        width: 90,
        renderFunction: value => <DateCell value={value} />,
        sorting: {
          onSort: sortDirection => sortToggle(['createdDate'], sortDirection),
        },
      }),
      TableHeaderCell.date({
        label: 'Laatst gewijzigd',
        dataProperty: 'lastModified',
        dataTestId: TEST_ID.LAST_MODIFIED,
        width: 110,
        sorting: {
          onSort: sortDirection => sortToggle(['lastModified'], sortDirection),
        },
        renderFunction: value => {
          const lastModifiedString = value
            ? formattedDateWithMonth(value, {
                showTodayAsLabel: true,
                showYesterdayAsLabel: true,
              })
            : text.notModifiedMessage;

          return <span>{lastModifiedString}</span>;
        },
      }),

      TableHeaderCell.custom({
        label: 'Gedeeld',
        dataProperty: 'shared',
        width: 75,
        sorting: {
          onSort: sortDirection =>
            sortToggle(['shared', 'enabled'], sortDirection),
        },
        dataTestId: TEST_ID.SHARED,
        renderFunction: (value: { enabled: boolean; id: string }) => (
          <ShareIndicator
            shared={value.enabled}
            onClick={() => navigate(`/-/automation/flows/sharing/${value.id}`)}
          />
        ),
      }),
      TableHeaderCell.boolean({
        label: 'Status',
        dataProperty: 'enabled',
        width: 75,
        sorting: {
          onSort: sortDirection => sortToggle(['enabled'], sortDirection),
        },
        renderFunction: value => <EnabledStatusIndicator value={value} />,
        dataTestId: TEST_ID.ENABLED,
      }),
      TableHeaderCell.action({
        dataTestId: TEST_ID.ACTION,
      }),
    ];

    if (hasErrors) {
      const errorColumn = TableHeaderCell.custom({
        label: '',
        dataProperty: 'hasErrors',
        width: 10,
        renderFunction: (value: boolean) => <ErrorsColumn hasErrors={value} />,
        sorting: {
          onSort: sortDirection => sortToggle(['hasErrors'], sortDirection),
        },
      });
      columns.splice(1, 0, errorColumn);
    }

    return columns;
  },
);

export default flowListColumns;
